import React, {useCallback} from 'react'
import {useForm} from 'react-hook-form'
import {useNiftyFiftyUpdate} from 'Event/template/NiftyFifty'
import ComponentConfig, {
  ComponentConfigProps,
  RemoveButton,
  SaveButton,
  Footer,
} from 'organization/Event/DashboardConfig/ComponentConfigPanel'
import {CountDownTimerSettings} from 'Event/Dashboard/components/CountDownTimer'
import {REMOVE} from 'Event/TemplateUpdateProvider'
import Settings from 'Event/template/NiftyFifty/Dashboard/CountDownTimers/CountDownTimer/TimerConfig/Settings'
import Styling from 'Event/template/NiftyFifty/Dashboard/CountDownTimers/CountDownTimer/TimerConfig/Styling'
import SettingsPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/SettingsPanel'
import StylingPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/StylingPanel'
import {useAddTranslation, useRemoveTranslations} from 'Event/LanguageProvider'
import {generateHashId} from 'lib/crypto/hash'
import {useAutoUpdate} from 'organization/Event/TemplateEditor'

export default function TimerConfig(
  props: ComponentConfigProps & {
    countDownTimer: CountDownTimerSettings
    id?: string
  },
) {
  const {showing: isVisible, onClose, id, countDownTimer} = props
  const updateTemplate = useNiftyFiftyUpdate()
  const removeTranslations = useRemoveTranslations()
  const addTranslation = useAddTranslation()

  const {control, handleSubmit, formState, watch} = useForm()

  const update = (id: string, updated: CountDownTimerSettings) => {
    updateTemplate({
      localization: addTranslation({
        [`countdowntimer_${id}_labels_days`]: updated.labels.days || '',
        [`countdowntimer_${id}_labels_hours`]: updated.labels.hours,
        [`countdowntimer_${id}_labels_minutes`]: updated.labels.minutes,
        [`countdowntimer_${id}_labels_seconds`]: updated.labels.seconds,
        [`countdowntimer_${id}_description`]: updated.description,
        [`countdowntimer_${id}_separator`]: updated.separator,
      }),
      countDownTimers: {
        [id]: {
          ...updated,
          labels: {
            days: `{{countdowntimer_${id}_labels_days}}`,
            hours: `{{countdowntimer_${id}_labels_hours}}`,
            minutes: `{{countdowntimer_${id}_labels_minutes}}`,
            seconds: `{{countdowntimer_${id}_labels_seconds}}`,
          },
          description: `{{countdowntimer_${id}_description}}`,
          separator: `{{countdowntimer_${id}_separator}}`,
        },
      },
    })
  }

  const removeCountDownTimer = useCallback(() => {
    if (!id) {
      throw new Error('Missing count down timer id')
    }

    updateTemplate({
      localization: removeTranslations([
        `countdowntimer_${id}_labels_days`,
        `countdowntimer_${id}_labels_hours`,
        `countdowntimer_${id}_labels_minutes`,
        `countdowntimer_${id}_labels_seconds`,
        `countdowntimer_${id}_description`,
        `countdowntimer_${id}_separator`,
      ]),
      countDownTimers: {
        [id]: REMOVE,
      },
    })
  }, [updateTemplate, id, removeTranslations])

  const save = (data: CountDownTimerSettings) => {
    if (id) {
      update(id, data)
    } else {
      generateHashId([
        'nieftyfifty_',
        'countdowntimer_',
        new Date().valueOf().toString(),
        Math.random().toString(),
      ]).then((id) => {
        update(id, data)
      })
    }

    onClose()
  }

  const showingRemoveButton = Boolean(id)

  useAutoUpdate({
    values: {countDownTimers: {[id!]: watch()}},
    when: Boolean(id),
  })

  return (
    <ComponentConfig
      showing={isVisible}
      onClose={onClose}
      title="Count Down Timer Config"
      onSubmit={handleSubmit(save)}
      hasChanges={formState.isDirty}
    >
      <SettingsPanel>
        <Settings control={control} countDownTimer={countDownTimer} />
      </SettingsPanel>
      <StylingPanel>
        <Styling control={control} countDownTimer={countDownTimer} />
      </StylingPanel>
      <Footer>
        <SaveButton type="submit" />
        <RemoveButton
          aria-label="remove countdown"
          onClick={removeCountDownTimer}
          showing={showingRemoveButton}
        />
      </Footer>
    </ComponentConfig>
  )
}
